'use client'

import { motion, useInView, useReducedMotion } from 'framer-motion'
import { useRef } from 'react'

import { Avatar, AvatarFallback, AvatarImage } from '@mdxui/primitives/avatar'

import { cn } from '../../lib/cn'
import { sectionVariants, headerVariants, cardVariants } from '../../lib/motion'

export interface TestimonialSingleProps {
  className?: string
  quote?: string
  author?: {
    name: string
    role: string
    avatar: {
      src: string
      alt: string
    }
  }
}

/**
 * TestimonialSingle - Centered single testimonial with large quote
 *
 * **IMPORTANT CONSTRAINTS FOR AI/PROGRAMMATIC GENERATION:**
 * - DO NOT use unless a "Testimonial" is explicitly defined in the input docs
 * - Requires actual quote, customer name, and role
 *
 * **Layout:**
 * ```
 * ┌────────────────────────────────────┐
 * │                                    │
 * │     "Large centered quote text"    │
 * │                                    │
 * │         [Avatar] Name              │
 * │                   Role             │
 * └────────────────────────────────────┘
 * ```
 *
 * **Best Use Cases:**
 * - Hero testimonials with single powerful quote
 * - Featured customer stories
 * - Social proof sections
 *
 * @example
 * ```tsx
 * <TestimonialSingle
 *   quote="This product changed everything for us."
 *   author={{
 *     name: "Jane Doe",
 *     role: "CEO at Company",
 *     avatar: { src: "/avatars/jane.jpg", alt: "Jane Doe" }
 *   }}
 * />
 * ```
 */
export function TestimonialSingle({
  className,
  quote = 'Lorem ipsum dolor sit amet consectetur adipisicing elit. Elig doloremque mollitia fugiat omnis! Porro facilis quo animi consequatur. Explicabo.',
  author = {
    name: 'Customer Name',
    role: 'Role',
    avatar: {
      src: '',
      alt: 'Customer Name',
    },
  },
}: TestimonialSingleProps) {
  const ref = useRef<HTMLElement>(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })
  const shouldReduceMotion = useReducedMotion()

  // Get initials from name for fallback
  const initials = author.name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

  return (
    <motion.section
      ref={ref}
      className={cn('py-32', className)}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
      variants={shouldReduceMotion ? undefined : sectionVariants}
    >
      <div className="container max-w-7xl mx-auto">
        <div className="flex flex-col items-center text-center">
          <motion.p
            className="mb-16 max-w-4xl px-8 text-xl font-normal md:text-2xl"
            variants={shouldReduceMotion ? undefined : headerVariants}
          >
            &ldquo;{quote}&rdquo;
          </motion.p>
          <motion.div
            className="flex items-center gap-2 md:gap-4"
            variants={shouldReduceMotion ? undefined : cardVariants}
          >
            <Avatar className="size-10 md:size-12">
              {author.avatar.src && (
                <AvatarImage src={author.avatar.src} alt={author.avatar.alt} />
              )}
              <AvatarFallback>{initials}</AvatarFallback>
            </Avatar>
            <div className="text-left">
              <p className="text-sm font-medium md:text-base">{author.name}</p>
              <p className="text-sm text-muted-foreground md:text-base">{author.role}</p>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.section>
  )
}
